import { useEffect, useRef, useState } from 'react';
import { inPartnerBox } from './partnerKontoAnmeldung';
import { istAppPortGate, meldeAppPortAn, PortKontenAntwort, PortTicketAntwort } from './appPortGate';
import { httpBoundary } from '@werkingflow/trust-boundary';

type Browser = HTMLWebViewElement & { getURL(): string; loadURL(url: string): Promise<unknown>; executeJavaScript(code: string): Promise<unknown> };
/** Prepare only this approved account box. The app login remains AppLoginPanel's job. */
export default function AppPortGateBootstrap({ ziel, konto, partition, buehne, panelId, aufnahme, mensch, webviewRef }: {
  ziel: string; konto: string; partition: string; buehne: string | null; panelId: string; aufnahme: boolean; mensch: string | null;
  webviewRef: React.RefObject<HTMLWebViewElement | null>;
}) {
  const [status, setStatus] = useState('');
  const scope = JSON.stringify([ziel, konto, partition, buehne, panelId, aufnahme, mensch]);
  const aktuelleScope = useRef(scope);
  aktuelleScope.current = scope;
  useEffect(() => {
    const wv = webviewRef.current as Browser | null;
    if (!wv) return;
    let aktiv = true, versucht = false;
    setStatus('');
    const nochHier = () => aktiv && aktuelleScope.current === scope && webviewRef.current === wv;
    const pruefen = async () => {
      if (!nochHier() || versucht || !istAppPortGate(wv.getURL(), ziel)) return;
      versucht = true;
      setStatus('Bereite die Anmeldung in diesem App-Fenster vor …');
      try {
        await inPartnerBox(partition, async () => {
          if (!nochHier()) return;
          const d = await httpBoundary({ name: 'cui.app-port.konten', url: '/api/guide/konten', schema: PortKontenAntwort,
            redirect: 'error', timeoutMs: 10000, onStatus: status => status >= 300 ? { throw: Error('Kontofreigabe für dieses App-Fenster nicht lesbar.') } : undefined });
          if (!nochHier()) return;
          const freigabe = d.konten?.find(k => k.kuerzel === konto);
          if (!freigabe) throw Error('Dieses App-Konto ist nicht freigegeben.');
          await meldeAppPortAn({ ziel, konto, freigabe, aufnahme, mensch, aktiv: nochHier,
            aktuelleUrl: () => wv.getURL(), navigate: url => wv.loadURL(url),
            ticket: async () => {
              return httpBoundary({ name: 'cui.app-port.ticket', url: '/api/fern-guide/ziel?ziel=' + encodeURIComponent('/?dgr=1'), schema: PortTicketAntwort,
                redirect: 'error', timeoutMs: 10000, onStatus: status => status >= 300 ? { throw: Error('Die eigene Partner-Anmeldung ist für diese Sitzung nicht verfügbar. Der Berater muss den Zugang im angemeldeten Nutzerfenster prüfen.') } : undefined });
            },
            identity: async () => {
              const result = await wv.executeJavaScript(`(async()=>{if(location.href!=="https://partner.werking.tools/?dgr=1")throw Error("Fenster gewechselt");const r=await fetch('/api/auth/me',{credentials:'include',redirect:'error',signal:AbortSignal.timeout(6000)});if(!r.ok)throw Error("Anmeldung nicht bestätigt");const d=await r.json();return {user:{id:d.user?.id},impersonatedBy:d.impersonatedBy};})()`);
              return result as { user?: { id?: string }; impersonatedBy?: unknown };
            },
          });
          if (nochHier()) setStatus('');
        });
      } catch (e) {
        if (nochHier()) setStatus(e instanceof Error ? e.message : 'App-Zugang konnte nicht vorbereitet werden.');
      }
    };
    const takt = () => { void pruefen(); };
    wv.addEventListener('dom-ready', takt); wv.addEventListener('did-navigate', takt);
    takt();
    return () => { aktiv = false; wv.removeEventListener('dom-ready', takt); wv.removeEventListener('did-navigate', takt); };
  }, [ziel, konto, partition, buehne, panelId, aufnahme, mensch, webviewRef]);
  return status ? <div role="status" data-ai-id="app-port-gate-status" style={{ position: 'absolute', top: 44, right: 8, maxWidth: 320, zIndex: 25, padding: 10, fontSize: 12, background: 'var(--tn-bg-dark)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)', borderRadius: 6 }}>{status}</div> : null;
}
